import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Stack,
  Divider,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import direccionClienteService from '../services/direccionClienteService';
import { DireccionCliente } from '../interfaces/DireccionCliente';
import LocalidadSearch from './LocalidadSearch';
import localidadService from '../services/localidadService';

interface Localidad {
  codigoCiudad: number;
  nombreCiudad?: string;
  codigoProvincia?: number;
  codigoRuta?: number;
  codigoPostal?: string;
  nombreProvincia?: string;
}

interface DireccionesClienteDialogProps {
  open: boolean;
  onClose: () => void;
  nroCliente: number;
  onDireccionesUpdated: () => void;
  direccionFacturacion?: string | null;
  direccionEnvio?: string | null;
  direccionResumen?: string | null;
  onAsignarDireccion?: (tipo: 'facturacion' | 'envio' | 'resumen', codigoDireccion: string | null) => void;
}

const DireccionesClienteDialog: React.FC<DireccionesClienteDialogProps> = ({
  open,
  onClose,
  nroCliente,
  onDireccionesUpdated,
  direccionFacturacion,
  direccionEnvio,
  direccionResumen,
  onAsignarDireccion,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [direcciones, setDirecciones] = useState<DireccionCliente[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingDireccion, setEditingDireccion] = useState<DireccionCliente | null | undefined>(undefined);
  const [openLocalidadSearch, setOpenLocalidadSearch] = useState(false);
  const [localidadSeleccionada, setLocalidadSeleccionada] = useState<Localidad | null>(null);
  const [formData, setFormData] = useState({
    codigoDireccion: '',
    direccion: '',
    codigoCiudad: null as number | null,
    telefono1: '',
    telefono2: '',
    email: '',
    emailPersonal: '',
    fax: '',
    vendedor: null as number | null,
    codigoRuta: null as number | null,
  });

  useEffect(() => {
    if (open && nroCliente) {
      loadDirecciones();
    }
  }, [open, nroCliente]);

  const loadDirecciones = async () => {
    setLoading(true);
    try {
      const data = await direccionClienteService.getDirecciones(nroCliente);
      setDirecciones(data || []);
    } catch (error: any) {
      enqueueSnackbar('Error al cargar direcciones', { variant: 'error' });
      console.error('Error loading direcciones:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setEditingDireccion(null);
    setFormData({
      codigoDireccion: '',
      direccion: '',
      codigoCiudad: null,
      telefono1: '',
      telefono2: '',
      email: '',
      emailPersonal: '',
      fax: '',
      vendedor: null,
      codigoRuta: null,
    });
    setLocalidadSeleccionada(null);
  };

  const handleOpenEdit = (direccion: DireccionCliente) => {
    setEditingDireccion(direccion);
    setFormData({
      codigoDireccion: direccion.codigoDireccion || '',
      direccion: direccion.direccion || '',
      codigoCiudad: direccion.codigoCiudad || null,
      telefono1: direccion.telefono1 || '',
      telefono2: direccion.telefono2 || '',
      email: direccion.email || '',
      emailPersonal: direccion.emailPersonal || '',
      fax: direccion.fax || '',
      vendedor: direccion.vendedor || null,
      codigoRuta: direccion.codigoRuta || null,
    });
    
    // Cargar localidad si existe
    if (direccion.codigoCiudad) {
      localidadService.getLocalidad(direccion.codigoCiudad)
        .then(loc => {
          setLocalidadSeleccionada({
            codigoCiudad: loc.codigoCiudad,
            nombreCiudad: loc.nombreCiudad,
            nombreProvincia: loc.nombreProvincia,
            codigoPostal: loc.codigoPostal,
          });
        })
        .catch(() => {
          // Si no se puede cargar, no es crítico
        });
    } else {
      setLocalidadSeleccionada(null);
    }
  };

  const handleSelectLocalidad = (localidad: Localidad) => {
    setLocalidadSeleccionada(localidad);
    setFormData(prev => ({
      ...prev,
      codigoCiudad: localidad.codigoCiudad,
    }));
    setOpenLocalidadSearch(false);
  };

  const handleSave = async () => {
    if (!formData.direccion.trim()) {
      enqueueSnackbar('La dirección es requerida', { variant: 'warning' });
      return;
    }
    if (!editingDireccion && !formData.codigoDireccion.trim()) {
      enqueueSnackbar('El código de dirección es requerido', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const direccionData: DireccionCliente = {
        nroCliente,
        codigoDireccion: editingDireccion ? editingDireccion.codigoDireccion : formData.codigoDireccion.trim(),
        direccion: formData.direccion.trim(),
        codigoCiudad: formData.codigoCiudad,
        telefono1: formData.telefono1 || null,
        telefono2: formData.telefono2 || null,
        email: formData.email || null,
        emailPersonal: formData.emailPersonal || null,
        fax: formData.fax || null,
        vendedor: formData.vendedor,
        codigoRuta: formData.codigoRuta,
      };

      if (editingDireccion) {
        await direccionClienteService.updateDireccion(
          nroCliente,
          editingDireccion.codigoDireccion,
          direccionData
        );
        enqueueSnackbar('Dirección actualizada correctamente', { variant: 'success' });
      } else {
        await direccionClienteService.createDireccion(nroCliente, direccionData);
        enqueueSnackbar('Dirección creada correctamente', { variant: 'success' });
      }

      await loadDirecciones();
      onDireccionesUpdated();
      setEditingDireccion(undefined);
      setFormData({
        codigoDireccion: '',
        direccion: '',
        codigoCiudad: null,
        telefono1: '',
        telefono2: '',
        email: '',
        emailPersonal: '',
        fax: '',
        vendedor: null,
        codigoRuta: null,
      });
      setLocalidadSeleccionada(null);
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || 'Error al guardar la dirección',
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (codigoDireccion: string) => {
    if (!window.confirm('¿Está seguro de eliminar esta dirección?')) {
      return;
    }

    try {
      await direccionClienteService.deleteDireccion(nroCliente, codigoDireccion);
      enqueueSnackbar('Dirección eliminada correctamente', { variant: 'success' });
      await loadDirecciones();
      onDireccionesUpdated();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || 'Error al eliminar la dirección',
        { variant: 'error' }
      );
    }
  };

  const handleClose = () => {
    setEditingDireccion(undefined);
    setFormData({
      codigoDireccion: '',
      direccion: '',
      codigoCiudad: null,
      telefono1: '',
      telefono2: '',
      email: '',
      emailPersonal: '',
      fax: '',
      vendedor: null,
      codigoRuta: null,
    });
    setLocalidadSeleccionada(null);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Gestionar Direcciones del Cliente</Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {/* Formulario de edición/creación */}
              {editingDireccion !== undefined && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      {editingDireccion ? 'Editar Dirección' : 'Nueva Dirección'}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Código Dirección"
                          value={formData.codigoDireccion}
                          onChange={(e) => setFormData(prev => ({ ...prev, codigoDireccion: e.target.value }))}
                          fullWidth
                          required
                          disabled={!!editingDireccion}
                          helperText={editingDireccion ? "El código de dirección no se puede modificar" : "Código único para identificar la dirección"}
                          sx={{
                            '& .MuiInputBase-input:disabled': {
                              bgcolor: '#f5f5f5',
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Localidad"
                          value={localidadSeleccionada 
                            ? `${localidadSeleccionada.nombreCiudad || ''} - ${localidadSeleccionada.nombreProvincia || ''}`
                            : ''}
                          fullWidth
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <IconButton
                                onClick={() => setOpenLocalidadSearch(true)}
                                edge="end"
                                color="primary"
                                size="small"
                                title="Buscar localidad"
                              >
                                <SearchIcon />
                              </IconButton>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Dirección"
                          value={formData.direccion}
                          onChange={(e) => setFormData(prev => ({ ...prev, direccion: e.target.value }))}
                          fullWidth
                          required
                          multiline
                          rows={2}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Teléfono 1"
                          value={formData.telefono1}
                          onChange={(e) => setFormData(prev => ({ ...prev, telefono1: e.target.value }))}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Teléfono 2"
                          value={formData.telefono2}
                          onChange={(e) => setFormData(prev => ({ ...prev, telefono2: e.target.value }))}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Email"
                          value={formData.email}
                          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                          fullWidth
                          type="email"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Email Personal"
                          value={formData.emailPersonal}
                          onChange={(e) => setFormData(prev => ({ ...prev, emailPersonal: e.target.value }))}
                          fullWidth
                          type="email"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Fax"
                          value={formData.fax}
                          onChange={(e) => setFormData(prev => ({ ...prev, fax: e.target.value }))}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Vendedor"
                          type="number"
                          value={formData.vendedor || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, vendedor: e.target.value ? Number(e.target.value) : null }))}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Código Ruta"
                          type="number"
                          value={formData.codigoRuta || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, codigoRuta: e.target.value ? Number(e.target.value) : null }))}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Stack direction="row" spacing={2}>
                          <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={saving}
                            startIcon={saving ? <CircularProgress size={20} /> : null}
                          >
                            {editingDireccion ? 'Actualizar' : 'Guardar'}
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setEditingDireccion(undefined);
                              setFormData({
                                codigoDireccion: '',
                                direccion: '',
                                codigoCiudad: null,
                                telefono1: '',
                                telefono2: '',
                                email: '',
                                emailPersonal: '',
                                fax: '',
                                vendedor: null,
                                codigoRuta: null,
                              });
                              setLocalidadSeleccionada(null);
                            }}
                          >
                            Cancelar
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}

              {/* Lista de direcciones */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1">Direcciones Registradas</Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenAdd}
                    disabled={editingDireccion !== undefined}
                  >
                    Agregar Dirección
                  </Button>
                </Box>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                        <TableRow>
                          <TableCell>Código</TableCell>
                          <TableCell>Dirección</TableCell>
                          <TableCell>Localidad</TableCell>
                          <TableCell>Teléfono</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell align="center">Facturación</TableCell>
                          <TableCell align="center">Entrega</TableCell>
                          <TableCell align="center">Resumen</TableCell>
                          <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                      {direcciones.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No hay direcciones registradas
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        direcciones.map((dir) => {
                          const codigoDir = dir.codigoDireccion.trim();
                          const esFacturacion = direccionFacturacion?.trim() === codigoDir;
                          const esEnvio = direccionEnvio?.trim() === codigoDir;
                          const esResumen = direccionResumen?.trim() === codigoDir;
                          
                          return (
                            <TableRow key={dir.codigoDireccion}>
                              <TableCell>{dir.codigoDireccion}</TableCell>
                              <TableCell>{dir.direccion}</TableCell>
                              <TableCell>
                                {dir.codigoCiudad ? `Código: ${dir.codigoCiudad}` : '-'}
                              </TableCell>
                              <TableCell>{dir.telefono1 || dir.telefono2 || '-'}</TableCell>
                              <TableCell>{dir.email || '-'}</TableCell>
                              <TableCell>
                                <Chip
                                  label="Activa"
                                  color="success"
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="center">
                                {esFacturacion ? (
                                  <Chip label="Asignada" color="primary" size="small" />
                                ) : (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                      if (onAsignarDireccion) {
                                        onAsignarDireccion('facturacion', dir.codigoDireccion);
                                        enqueueSnackbar('Dirección asignada a Facturación', { variant: 'success' });
                                      }
                                    }}
                                  >
                                    Asignar
                                  </Button>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {esEnvio ? (
                                  <Chip label="Asignada" color="primary" size="small" />
                                ) : (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                      if (onAsignarDireccion) {
                                        onAsignarDireccion('envio', dir.codigoDireccion);
                                        enqueueSnackbar('Dirección asignada a Entrega', { variant: 'success' });
                                      }
                                    }}
                                  >
                                    Asignar
                                  </Button>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {esResumen ? (
                                  <Chip label="Asignada" color="primary" size="small" />
                                ) : (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                      if (onAsignarDireccion) {
                                        onAsignarDireccion('resumen', dir.codigoDireccion);
                                        enqueueSnackbar('Dirección asignada a Resumen', { variant: 'success' });
                                      }
                                    }}
                                  >
                                    Asignar
                                  </Button>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenEdit(dir)}
                                  color="primary"
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(dir.codigoDireccion)}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <LocalidadSearch
        open={openLocalidadSearch}
        onClose={() => setOpenLocalidadSearch(false)}
        onSelect={handleSelectLocalidad}
      />
    </>
  );
};

export default DireccionesClienteDialog;

